import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TrendingUp, BarChart3, AlertTriangle, Download, Loader2, Wallet, Users } from 'lucide-react';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { getAnalyticsReport } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import jsPDF from 'jspdf';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Analytics = () => {
  const { t } = useLanguage();
  const texts = t('analytics');

  const PERIOD_OPTIONS = [
    { key: '7', label: texts.last7Days || 'Last 7 Days', days: 7 },
    { key: '30', label: texts.last30Days || 'Last 30 Days', days: 30 },
    { key: '90', label: texts.last90Days || 'Last 90 Days', days: 90 },
    { key: '365', label: texts.lastYear || 'Last Year', days: 365 }
  ];

  const [selectedPeriodKey, setSelectedPeriodKey] = useState('30');
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [error, setError] = useState(null);
  const fetchKeyRef = useRef(null);
  const isFetchingRef = useRef(false);

  const selectedPeriod = PERIOD_OPTIONS.find((p) => p.key === selectedPeriodKey) || PERIOD_OPTIONS[1];

  const getBusinessId = useCallback(() => {
    const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
    return selectedBusiness?.id;
  }, []);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₹0';
    return `₹${Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return '0%';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${Number(value).toFixed(1)}%`;
  };

  const fetchAnalyticsData = useCallback(async (days) => {
    const businessId = getBusinessId();
    if (!businessId) {
      setError('No business selected. Please select a business first.');
      setLoading(false);
      return;
    }

    const currentFetchKey = `${businessId}-${days}`;
    if (isFetchingRef.current || fetchKeyRef.current === currentFetchKey) {
      return;
    }

    isFetchingRef.current = true;
    fetchKeyRef.current = currentFetchKey;

    try {
      setLoading(true);
      setError(null);
      const response = await getAnalyticsReport(businessId, days);
      if (response.success) {
        setAnalyticsData(response.data);
      } else {
        setError(response.message || 'Failed to fetch analytics data');
      }
    } catch (err) {
      setError('An error occurred while fetching analytics data');
      console.error('Error fetching analytics data:', err);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [getBusinessId]);

  useEffect(() => {
    fetchAnalyticsData(selectedPeriod.days);
  }, [selectedPeriodKey, selectedPeriod.days, fetchAnalyticsData]);

  const getKPIData = () => {
    if (!analyticsData?.metrics) {
      return [
        { title: texts.loanGrowth || 'Loan Growth', value: '0', change: '0% vs last period', changeType: 'positive', icon: TrendingUp, color: 'green' },
        { title: texts.activePortfolioValue || 'Active Portfolio Value', value: '₹0', change: '0% vs last period', changeType: 'positive', icon: Wallet, color: 'blue' },
        { title: texts.collectionRate || 'Collection Rate', value: '0%', change: '0% vs last period', changeType: 'positive', icon: BarChart3, color: 'yellow' },
        { title: texts.riskScore || 'Risk Score', value: '0', change: '0% vs last period', changeType: 'negative', icon: AlertTriangle, color: 'red' }
      ];
    }

    const { metrics } = analyticsData;
    const loanGrowth = metrics.loanGrowth || metrics.newLoans;

    return [
      {
        title: texts.loanGrowth || 'Loan Growth',
        value: String(loanGrowth?.value || 0),
        change: `${formatPercentage(loanGrowth?.change || 0)} vs last period`,
        changeType: (loanGrowth?.change || 0) >= 0 ? 'positive' : 'negative',
        icon: TrendingUp,
        color: 'green'
      },
      {
        title: texts.activePortfolioValue || 'Active Portfolio Value',
        value: formatCurrency(metrics.activePortfolioValue?.value || 0),
        change: `${formatPercentage(metrics.activePortfolioValue?.change || 0)} vs last period`,
        changeType: (metrics.activePortfolioValue?.change || 0) >= 0 ? 'positive' : 'negative',
        icon: Wallet,
        color: 'blue'
      },
      {
        title: texts.collectionRate || 'Collection Rate',
        value: `${Number(metrics.collectionRate?.value || 0).toFixed(1)}%`,
        change: `${formatPercentage(metrics.collectionRate?.change || 0)} vs last period`,
        changeType: (metrics.collectionRate?.change || 0) >= 0 ? 'positive' : 'negative',
        icon: BarChart3,
        color: 'yellow'
      },
      {
        title: texts.riskScore || 'Risk Score',
        value: String(metrics.riskScore?.value || 0),
        change: `${formatPercentage(metrics.riskScore?.change || 0)} vs last period`,
        changeType: (metrics.riskScore?.change || 0) >= 0 ? 'negative' : 'positive',
        icon: AlertTriangle,
        color: 'red'
      }
    ];
  };

  const getCollectionTrendChartData = () => {
    const trend = analyticsData?.collectionPerformance?.collectionTrend
      || analyticsData?.revenueTrend
      || [];
    if (!trend.length) return null;

    return {
      labels: trend.map((item) => {
        const date = new Date(item.date);
        return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
      }),
      datasets: [
        {
          label: texts.collections || 'Collections',
          data: trend.map((item) => item.revenue || 0),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 5
        }
      ]
    };
  };

  const getLoanStatusChartData = () => {
    if (!analyticsData?.loanStatusDistribution) return null;
    const distribution = analyticsData.loanStatusDistribution;
    const total = (distribution.awaited || 0) + (distribution.active || 0) + (distribution.completed || 0);
    if (total === 0) return null;

    return {
      labels: [
        texts.awaited || 'Awaited',
        texts.active || 'Active',
        texts.completed || 'Completed'
      ],
      datasets: [
        {
          data: [distribution.awaited || 0, distribution.active || 0, distribution.completed || 0],
          backgroundColor: [
            'rgba(251, 191, 36, 0.8)',
            'rgba(59, 130, 246, 0.8)',
            'rgba(34, 197, 94, 0.8)'
          ],
          borderColor: ['rgb(251, 191, 36)', 'rgb(59, 130, 246)', 'rgb(34, 197, 94)'],
          borderWidth: 2
        }
      ]
    };
  };

  const collectionChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `${texts.collections || 'Collections'}: ${formatCurrency(context.parsed.y)}`
        }
      }
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => {
            if (value >= 1000000) return `₹${(value / 1000000).toFixed(1)}M`;
            if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
            return `₹${value}`;
          }
        }
      }
    }
  };

  const loanStatusChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total ? ((value / total) * 100).toFixed(1) : 0;
            return `${context.label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  const handleExport = () => {
    if (!analyticsData) {
      alert('No data available to export');
      return;
    }

    try {
      const doc = new jsPDF();
      let y = 20;
      const margin = 20;

      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Business Analytics Report', margin, y);
      y += 8;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`Period: ${selectedPeriod.label}`, margin, y);
      y += 6;
      doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, margin, y);
      y += 12;

      const metrics = analyticsData.metrics || {};
      const loanGrowth = metrics.loanGrowth || metrics.newLoans;
      const rows = [
        ['Loan Growth', String(loanGrowth?.value || 0), formatPercentage(loanGrowth?.change || 0)],
        ['Active Portfolio Value', formatCurrency(metrics.activePortfolioValue?.value || 0), formatPercentage(metrics.activePortfolioValue?.change || 0)],
        ['Collection Rate', `${Number(metrics.collectionRate?.value || 0).toFixed(1)}%`, formatPercentage(metrics.collectionRate?.change || 0)],
        ['Risk Score', String(metrics.riskScore?.value || 0), formatPercentage(metrics.riskScore?.change || 0)]
      ];

      doc.setFont('helvetica', 'bold');
      doc.text('Key Metrics', margin, y);
      y += 8;
      doc.setFont('helvetica', 'normal');
      rows.forEach(([label, value, change]) => {
        doc.text(`${label}: ${value} (${change})`, margin, y);
        y += 6;
      });

      if (analyticsData.portfolioSummary) {
        y += 6;
        doc.setFont('helvetica', 'bold');
        doc.text('Portfolio Summary', margin, y);
        y += 8;
        doc.setFont('helvetica', 'normal');
        const ps = analyticsData.portfolioSummary;
        [
          `Active Loans: ${ps.activeLoans || 0}`,
          `Completed Loans: ${ps.completedLoans || 0}`,
          `Overdue Loans: ${ps.overdueLoans || 0}`,
          `Outstanding Principal: ${formatCurrency(ps.outstandingPrincipal)}`
        ].forEach((line) => {
          doc.text(line, margin, y);
          y += 6;
        });
      }

      if (analyticsData.customerInsights) {
        y += 6;
        doc.setFont('helvetica', 'bold');
        doc.text('Customer Insights', margin, y);
        y += 8;
        doc.setFont('helvetica', 'normal');
        const ci = analyticsData.customerInsights;
        [
          `New Customers: ${ci.newCustomers || 0}`,
          `Repeat Customers: ${ci.repeatCustomers || 0}`,
          `Average Loan Size: ${formatCurrency(ci.averageLoanSize)}`
        ].forEach((line) => {
          doc.text(line, margin, y);
          y += 6;
        });
      }

      doc.save(`analytics-report-${selectedPeriod.key}d-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      alert('Failed to export report. Please try again.');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Loader2 className="spin" size={48} color="#3b82f6" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ color: '#ef4444', marginBottom: '1rem' }}>{error}</div>
        <button
          onClick={() => {
            fetchKeyRef.current = null;
            isFetchingRef.current = false;
            fetchAnalyticsData(selectedPeriod.days);
          }}
          className="btn btn-primary"
        >
          Retry
        </button>
      </div>
    );
  }

  const kpiData = getKPIData();
  const collectionChartData = getCollectionTrendChartData();
  const loanStatusChartData = getLoanStatusChartData();
  const portfolio = analyticsData?.portfolioSummary;
  const collection = analyticsData?.collectionPerformance;
  const customers = analyticsData?.customerInsights;

  const sectionTitleStyle = {
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--text-secondary)',
    margin: '0 0 1.25rem'
  };

  const cardHeadingStyle = {
    margin: '0 0 1.25rem'
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <select
          className="form-select"
          value={selectedPeriodKey}
          onChange={(e) => {
            fetchKeyRef.current = null;
            isFetchingRef.current = false;
            setSelectedPeriodKey(e.target.value);
          }}
          style={{ width: 'auto' }}
        >
          {PERIOD_OPTIONS.map((period) => (
            <option key={period.key} value={period.key}>{period.label}</option>
          ))}
        </select>

        <button
          className="btn btn-secondary"
          style={{ backgroundColor: '#000000', color: 'white', border: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          onClick={handleExport}
        >
          <Download size={16} />
          {texts.exportReport || 'Export Report'}
        </button>
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <div className="analytics-section-title" style={sectionTitleStyle}>{texts.businessPerformance || 'Business Performance'}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
          {kpiData.map((kpi, index) => {
            const Icon = kpi.icon;
            const accent = kpi.color === 'blue' ? '#3b82f6' : kpi.color === 'green' ? '#10b981' : kpi.color === 'yellow' ? '#f59e0b' : '#ef4444';
            return (
              <div key={index} className="kpi-card" style={{ borderLeft: `4px solid ${accent}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div className="kpi-value">{kpi.value}</div>
                    <div className="kpi-label">{kpi.title}</div>
                    <div className="kpi-subtitle" style={{ color: kpi.changeType === 'positive' ? '#10b981' : '#ef4444' }}>
                      {kpi.change}
                    </div>
                  </div>
                  <div style={{ color: accent }}>
                    <Icon size={24} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {portfolio && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="analytics-section-title" style={sectionTitleStyle}>{texts.portfolioHealth || 'Portfolio Health'}</div>
          <h3 className="card-title" style={cardHeadingStyle}>{texts.portfolioSummary || 'Portfolio Summary'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
            {[
              { label: texts.activeLoans || 'Active Loans', value: portfolio.activeLoans || 0 },
              { label: texts.completedLoans || 'Completed Loans', value: portfolio.completedLoans || 0 },
              { label: texts.overdueLoans || 'Overdue Loans', value: portfolio.overdueLoans || 0 },
              { label: texts.outstandingPrincipal || 'Outstanding Principal', value: formatCurrency(portfolio.outstandingPrincipal) }
            ].map((item) => (
              <div key={item.label} className="theme-panel" style={{ padding: '1rem' }}>
                <div className="theme-heading" style={{ fontSize: '0.8rem', opacity: 0.75 }}>{item.label}</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 600, marginTop: '0.25rem' }}>{item.value}</div>
              </div>
            ))}
          </div>
          {loanStatusChartData && (
            <div style={{ height: '260px', position: 'relative', marginTop: '1.25rem' }}>
              <Doughnut data={loanStatusChartData} options={loanStatusChartOptions} />
            </div>
          )}
        </div>
      )}

      {customers && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="analytics-section-title" style={sectionTitleStyle}>{texts.customerInsights || 'Customer Insights'}</div>
          <h3 className="card-title" style={{ ...cardHeadingStyle, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Users size={18} />
            {texts.customerInsights || 'Customer Insights'}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div className="theme-panel-soft" style={{ padding: '1rem', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{texts.newCustomers || 'New Customers'}</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{customers.newCustomers || 0}</div>
            </div>
            <div className="theme-panel-soft" style={{ padding: '1rem', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{texts.repeatCustomers || 'Repeat Customers'}</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{customers.repeatCustomers || 0}</div>
            </div>
            <div className="theme-panel-soft" style={{ padding: '1rem', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{texts.averageLoanSize || 'Average Loan Size'}</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{formatCurrency(customers.averageLoanSize)}</div>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="analytics-section-title" style={sectionTitleStyle}>{texts.collectionPerformance || 'Collection Performance'}</div>
        <h3 className="card-title" style={cardHeadingStyle}>{texts.collectionPerformance || 'Collection Performance'}</h3>
        <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{texts.recoveryRate || 'Recovery Rate'}</div>
            <div style={{ fontWeight: 600 }}>{Number(collection?.recoveryRate || analyticsData?.metrics?.collectionRate?.value || 0).toFixed(1)}%</div>
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{texts.latePayments || 'Late Payments'}</div>
            <div style={{ fontWeight: 600 }}>{collection?.latePayments || 0}</div>
          </div>
        </div>
        <div style={{ height: '280px', position: 'relative' }}>
          {collectionChartData ? (
            <Line data={collectionChartData} options={collectionChartOptions} />
          ) : (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
              No collection trend data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
